export type PlaylistEntry = {
    timestamp: string;
    videoId: string;
    title: string;
    artist: string;
    thumbnailUrl: string;
    youtubeUrl: string;
};

export type YouTubeSearchResult = {
    videoId: string;
    title: string;
    artist: string;
    thumbnailUrl: string;
    youtubeUrl: string;
};

/**
 * Extract YouTube video ID from various URL formats
 */
export function extractYouTubeVideoId(url: string): string | null {
    if (!url) return null;

    // Handle various YouTube URL formats
    const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
        /youtube\.com\/playlist\?list=([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match && match[1]) {
            return match[1];
        }
    }

    // If it's already just a video ID (11 characters, alphanumeric)
    if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
        return url;
    }

    return null;
}

/**
 * Generate YouTube thumbnail URL from video ID
 */
export function getYouTubeThumbnail(videoId: string): string {
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

/**
 * Fetch playlist entries from Google Sheets
 */
export async function fetchPlaylist(apiUrl: string): Promise<PlaylistEntry[]> {
    try {
        const response = await fetch(apiUrl, {
            method: 'GET',
        });

        // Handle network errors (status 0 indicates network/CORS error)
        if (response.status === 0) {
            console.error('Playlist API network error (status 0):', {
                url: apiUrl,
                type: response.type,
            });
            throw new Error('Network error: Failed to connect to playlist API. This may be a CORS issue or the server is unreachable. Check that the proxy is configured correctly.');
        }

        // Handle non-OK responses
        if (!response.ok) {
            let errorText = response.statusText;
            try {
                const responseText = await response.text();
                // Try to parse as JSON to get structured error
                try {
                    const errorData = JSON.parse(responseText);
                    if (errorData.error) {
                        errorText = errorData.error;
                        if (errorData.details) {
                            errorText += ` ${errorData.details}`;
                        }
                    } else {
                        errorText = responseText;
                    }
                } catch {
                    errorText = responseText || response.statusText || `HTTP ${response.status}`;
                }
            } catch (e) {
                errorText = response.statusText || `HTTP ${response.status}`;
            }
            throw new Error(`Failed to fetch playlist: ${response.status} ${errorText}`);
        }

        // Get response text first to handle non-JSON responses
        const responseText = await response.text();
        
        // Check if response is empty
        if (!responseText.trim()) {
            console.warn('Empty response from playlist API');
            return [];
        }

        // Try to parse as JSON
        let data: any;
        try {
            data = JSON.parse(responseText);
        } catch (parseError) {
            // If parsing fails, check if it's HTML (error page)
            if (responseText.trim().startsWith('<!') || responseText.includes('<html')) {
                // Try to extract a meaningful error from HTML
                const titleMatch = responseText.match(/<title[^>]*>([^<]+)<\/title>/i);
                const h1Match = responseText.match(/<h1[^>]*>([^<]+)<\/h1>/i);
                const errorText = titleMatch?.[1] || h1Match?.[1] || 'HTML error page received';
                
                console.error('Playlist API returned HTML error page:', {
                    status: response.status,
                    contentType: response.headers.get('content-type'),
                    errorTitle: errorText,
                    responsePreview: responseText.substring(0, 500),
                });
                
                throw new Error(`Playlist API error: ${errorText}. The Google Apps Script may not be deployed correctly.`);
            }
            
            // If parsing fails, log the actual response for debugging
            console.error('Failed to parse playlist response as JSON:', {
                status: response.status,
                contentType: response.headers.get('content-type'),
                responsePreview: responseText.substring(0, 500),
            });
            throw new Error(`Invalid JSON response from playlist API: ${responseText.substring(0, 200)}`);
        }
        
        if (data.result === 'error') {
            throw new Error(data.error || 'Unknown error fetching playlist');
        }

        // Transform sheet rows to PlaylistEntry objects
        if (Array.isArray(data.entries)) {
            const entries = data.entries.map((entry: any) => ({
                timestamp: entry.timestamp || '',
                videoId: entry.videoId || entry.VIDEO_ID || '',
                title: entry.title || entry.TITLE || '',
                artist: entry.artist || entry.ARTIST || '',
                thumbnailUrl: entry.thumbnailUrl || entry.THUMBNAIL_URL || '',
                youtubeUrl: entry.youtubeUrl || entry.YOUTUBE_URL || '',
            }));
            console.log('[fetchPlaylist] Transformed entries:', entries.map(e => ({
                videoId: e.videoId,
                youtubeUrl: e.youtubeUrl,
                title: e.title,
            })));
            return entries;
        }

        console.warn('[fetchPlaylist] No entries array in response:', data);
        return [];
    } catch (error) {
        console.error('Error fetching playlist:', error);
        throw error;
    }
}

/**
 * Add a song to the playlist via Google Sheets API
 */
export async function addSongToPlaylist(
    apiUrl: string,
    song: YouTubeSearchResult,
    signal?: AbortSignal
): Promise<void> {
    try {
        const formData = new FormData();
        formData.append('videoId', song.videoId);
        formData.append('title', song.title);
        formData.append('artist', song.artist);
        formData.append('thumbnailUrl', song.thumbnailUrl);
        formData.append('youtubeUrl', song.youtubeUrl);

        const response = await fetch(apiUrl, {
            method: 'POST',
            body: formData,
            signal,
        });

        // Handle network errors (status 0 indicates network/CORS error)
        if (response.status === 0) {
            console.error('Add song network error (status 0):', {
                url: apiUrl,
                type: response.type,
            });
            throw new Error('Network error: Failed to connect to playlist API. This may be a CORS issue or the server is unreachable. Check that the proxy is configured correctly.');
        }

        // Check if response indicates success (200-299)
        const isSuccess = response.status >= 200 && response.status < 300;

        if (!isSuccess) {
            let errorText = response.statusText;
            try {
                const responseText = await response.text();
                // Try to parse as JSON to get structured error
                try {
                    const errorData = JSON.parse(responseText);
                    if (errorData.error) {
                        errorText = errorData.error;
                        if (errorData.details) {
                            errorText += ` ${errorData.details}`;
                        }
                    } else {
                        errorText = responseText;
                    }
                } catch {
                    errorText = responseText || response.statusText || `HTTP ${response.status}`;
                }
            } catch (e) {
                errorText = response.statusText || `HTTP ${response.status}`;
            }
            throw new Error(`Failed to add song: ${response.status} ${errorText}`);
        }

        // For successful responses, try to parse JSON but be lenient
        let responseText = '';
        try {
            responseText = await response.text();
        } catch (e) {
            // If we can't read the response but status is success, consider it a success
            console.warn('Could not read response body, but status indicates success:', response.status);
            return; // Success - operation completed
        }

        // If response is empty but status is success, consider it success
        if (!responseText.trim()) {
            console.warn('Empty response from add song, but status indicates success:', response.status);
            return; // Success - operation completed
        }

        // Try to parse as JSON, but don't fail if it's not JSON
        let data: any;
        try {
            data = JSON.parse(responseText);
        } catch (parseError) {
            // If parsing fails but status is success, log warning but don't throw error
            console.warn('Could not parse add song response as JSON, but status indicates success:', {
                status: response.status,
                contentType: response.headers.get('content-type'),
                responsePreview: responseText.substring(0, 200),
            });
            // If status is success, consider it a success even if we can't parse JSON
            return; // Success - operation completed
        }
        
        // Only throw error if we explicitly get an error result in the JSON
        if (data && data.result === 'error') {
            throw new Error(data.error || 'Unknown error adding song');
        }

        // If we got here, it's a success (either no data, or data.result is not 'error')
        return; // Success
    } catch (error) {
        // Handle AbortError gracefully - don't throw if request was intentionally aborted
        if (error instanceof Error && error.name === 'AbortError') {
            console.log('Add song request was aborted');
            return; // Silently return - request was cancelled intentionally
        }
        console.error('Error adding song to playlist:', error);
        throw error;
    }
}

